//src/components/pendent-load/PendentLoadAuditPanel.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  createPortal,
} from "react-dom";

import EmojiPicker from "emoji-picker-react";

import {
  CheckCircle2,
  Clock3,
  FileCheck2,
  Filter,
  Info,
  Navigation,
  Package,
  Paperclip,
  Send,
  Smile,
} from "lucide-react";

import axios from "../../axiosConfig";

import {
  useTranslation,
} from "react-i18next";

import {
  getSocket,
} from "../../lib/socket";

const EMOJI_PICKER_WIDTH =
  292;

const EMOJI_PICKER_HEIGHT =
  326;

const EMOJI_PICKER_VIEWPORT_PADDING =
  10;

const EMOJI_PICKER_GAP =
  8;

function getInitials(
  value
) {
  const name =
    String(
      value ||
        ""
    ).trim();

  if (
    !name
  ) {
    return "SH";
  }

  return name
    .split(
      /\s+/
    )
    .slice(
      0,
      2
    )
    .map(
      (
        part
      ) =>
        part[0]
    )
    .join(
      ""
    )
    .toUpperCase();
}

function formatDateTime(
  value,
  locale,
  nowLabel
) {
  if (
    !value
  ) {
    return nowLabel;
  }

  const date =
    new Date(
      value
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(
      value
    );
  }

  return new Intl.DateTimeFormat(
    locale,
    {
      day:
        "2-digit",

      month:
        "2-digit",

      year:
        "numeric",

      hour:
        "2-digit",

      minute:
        "2-digit",
    }
  ).format(
    date
  );
}

function getMessageIdentity(
  message,
  fallbackIndex
) {
  if (
    message?.id !==
      null &&
    message?.id !==
      undefined
  ) {
    return `id:${message.id}`;
  }

  return [
    "fallback",
    message
      ?.requestConversationId ||
      "",
    message
      ?.senderType ||
      "",
    message
      ?.createdAt ||
      "",
    message
      ?.message ||
      "",
    fallbackIndex,
  ].join(
    ":"
  );
}

function mergeChatMessages(
  ...messageGroups
) {
  const mergedMessages =
    [];

  const indexByIdentity =
    new Map();

  messageGroups.forEach(
    (
      messageGroup
    ) => {
      const safeMessages =
        Array.isArray(
          messageGroup
        )
          ? messageGroup
          : [];

      safeMessages.forEach(
        (
          message,
          messageIndex
        ) => {
          if (
            !message ||
            typeof message !==
              "object"
          ) {
            return;
          }

          const identity =
            getMessageIdentity(
              message,
              messageIndex
            );

          if (
            indexByIdentity.has(
              identity
            )
          ) {
            const existingIndex =
              indexByIdentity.get(
                identity
              );

            mergedMessages[
              existingIndex
            ] = {
              ...mergedMessages[
                existingIndex
              ],

              ...message,
            };

            return;
          }

          indexByIdentity.set(
            identity,
            mergedMessages.length
          );

          mergedMessages.push(
            message
          );
        }
      );
    }
  );

  return mergedMessages
    .map(
      (
        message,
        originalIndex
      ) => {
        const timestamp =
          Date.parse(
            message
              ?.createdAt ||
              message
                ?.updatedAt ||
              ""
          );

        return {
          message,

          originalIndex,

          timestamp:
            Number.isFinite(
              timestamp
            )
              ? timestamp
              : 0,
        };
      }
    )
    .sort(
      (
        entryA,
        entryB
      ) => {
        if (
          entryA.timestamp !==
          entryB.timestamp
        ) {
          return (
            entryA.timestamp -
            entryB.timestamp
          );
        }

        const idA =
          Number(
            entryA
              .message
              ?.id
          );

        const idB =
          Number(
            entryB
              .message
              ?.id
          );

        if (
          Number.isFinite(
            idA
          ) &&
          Number.isFinite(
            idB
          ) &&
          idA !==
            idB
        ) {
          return (
            idA -
            idB
          );
        }

        return (
          entryA.originalIndex -
          entryB.originalIndex
        );
      }
    )
    .map(
      (
        entry
      ) =>
        entry.message
    );
}

export default function PendentLoadAuditPanel({
  requestId,
  pendentLoadNumber,
  pickupCount = 0,
  deliveryCount = 0,
  merchandise,
  currentPendentLoad,
  showAudit = true,
}) {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "edit_pendentload"
    );

  const locale =
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt";

  const normalizedRequestId =
    useMemo(
      () => {
        const numericRequestId =
          Number(
            requestId
          );

        return (
          Number.isInteger(
            numericRequestId
          ) &&
          numericRequestId >
            0
        )
          ? numericRequestId
          : null;
      },
      [
        requestId,
      ]
    );

  const [
    conversation,
    setConversation,
  ] =
    useState(
      null
    );

  const [
    chatMessages,
    setChatMessages,
  ] =
    useState(
      []
    );

  const [
    messageText,
    setMessageText,
  ] =
    useState(
      ""
    );

  const [
    isLoadingMessages,
    setIsLoadingMessages,
  ] =
    useState(
      false
    );

  const [
    isSendingMessage,
    setIsSendingMessage,
  ] =
    useState(
      false
    );

  const [
    messageError,
    setMessageError,
  ] =
    useState(
      ""
    );

  const [
    isEmojiPickerOpen,
    setIsEmojiPickerOpen,
  ] =
    useState(
      false
    );

  const [
    emojiPickerPosition,
    setEmojiPickerPosition,
  ] =
    useState({
      top:
        12,

      left:
        12,
    });

  const messageInputRef =
    useRef(
      null
    );

  const emojiPickerRef =
    useRef(
      null
    );

  const emojiPickerPopoverRef =
    useRef(
      null
    );

  const chatScrollRef =
    useRef(
      null
    );

  const conversationIdRef =
    useRef(
      ""
    );

  const realtimeWasDisconnectedRef =
    useRef(
      false
    );

  const shipperName =
    String(
      currentPendentLoad
        ?.shipperCompanyName ||
        currentPendentLoad
          ?.shipperCompany
          ?.name ||
        currentPendentLoad
          ?.shipperName ||
        currentPendentLoad
          ?.clientCompanyName ||
        t(
          "auditPanel.shipperFallback"
        )
    ).trim();

  const shipperInitials =
    getInitials(
      shipperName
    );

  const auditItems =
    useMemo(
      () => [
        {
          type:
            "success",

          icon:
            CheckCircle2,

          title:
            t(
              "auditPanel.audit.requestReceived"
            ),

          meta:
            formatDateTime(
              currentPendentLoad
                ?.createdAt,
              locale,
              t(
                "common.now"
              )
            ),
        },

        {
          type:
            "active",

          icon:
            Navigation,

          title:
            t(
              "auditPanel.audit.routeConfigured"
            ),

          meta:
            t(
              "auditPanel.audit.stopCounts",
              {
                pickupCount,
                deliveryCount,
              }
            ),
        },

        {
          type:
            "info",

          icon:
            Package,

          title:
            t(
              "detailsPanel.fields.merchandise"
            ),

          meta:
            merchandise ||
            t(
              "common.undefined"
            ),
        },
      ],
      [
        currentPendentLoad
          ?.createdAt,
        deliveryCount,
        locale,
        merchandise,
        pickupCount,
        t,
      ]
    );

  useEffect(
    () => {
      conversationIdRef.current =
        String(
          conversation
            ?.id ||
            ""
        );
    },
    [
      conversation,
    ]
  );

  useEffect(
    () => {
      const scrollElement =
        chatScrollRef.current;

      if (
        !scrollElement
      ) {
        return;
      }

      scrollElement.scrollTop =
        scrollElement.scrollHeight;
    },
    [
      chatMessages,
      isLoadingMessages,
    ]
  );

  useEffect(
    () => {
      if (
        !normalizedRequestId
      ) {
        setConversation(
          null
        );

        setChatMessages(
          []
        );

        setMessageError(
          ""
        );

        setIsLoadingMessages(
          false
        );

        return undefined;
      }

      let disposed =
        false;

      const controller =
        new AbortController();

      const loadMessages =
        async (
          silent = false
        ) => {
          if (
            !silent
          ) {
            setIsLoadingMessages(
              true
            );
          }

          try {
            const response =
              await axios.get(
                `/carrier-request-conversations/${normalizedRequestId}/messages`,
                {
                  signal:
                    controller.signal,
                }
              );

            if (
              disposed
            ) {
              return;
            }

            setConversation(
              response.data
                ?.conversation ||
                null
            );

            setChatMessages(
              (
                currentMessages
              ) =>
                mergeChatMessages(
                  currentMessages,
                  response.data
                    ?.messages
                )
            );

            setMessageError(
              ""
            );
          } catch (
            error
          ) {
            if (
              disposed ||
              error?.code ===
                "ERR_CANCELED"
            ) {
              return;
            }

            console.error(
              "[PendentLoadAuditPanel] Could not load chat:",
              error
            );

            if (
              error
                ?.response
                ?.status ===
              404
            ) {
              setConversation(
                null
              );

              setChatMessages(
                []
              );

              setMessageError(
                t(
                  "auditPanel.errors.noActiveConversation"
                )
              );

              return;
            }

            setMessageError(
              t(
                "auditPanel.errors.loadConversationFailed"
              )
            );
          } finally {
            if (
              !disposed
            ) {
              setIsLoadingMessages(
                false
              );
            }
          }
        };

      void loadMessages(
        false
      );

      const socket =
        getSocket();

      const subscribe =
        () => {
          socket.emit(
            "carrier:request:subscribe",
            {
              requestId:
                normalizedRequestId,
            }
          );
        };

      const unsubscribe =
        () => {
          socket.emit(
            "carrier:request:unsubscribe",
            {
              requestId:
                normalizedRequestId,
            }
          );
        };

      const handleSubscribed =
        (
          event
        ) => {
          if (
            Number(
              event
                ?.requestId
            ) !==
            normalizedRequestId
          ) {
            return;
          }

          if (
            event?.ok ===
            false
          ) {
            setMessageError(
              event?.message ||
                t(
                  "auditPanel.errors.realtimeOpenFailed"
                )
            );

            return;
          }

          if (
            event
              ?.conversationId
          ) {
            setConversation(
              (
                currentConversation
              ) => ({
                ...currentConversation,

                id:
                  event
                    .conversationId,

                requestId:
                  normalizedRequestId,

                shipperCompanyId:
                  event
                    .shipperCompanyId,

                carrierCompanyId:
                  event
                    .carrierCompanyId,

                status:
                  "ACTIVE",
              })
            );
          }
        };

      const handleMessageCreated =
        (
          event
        ) => {
          if (
            Number(
              event
                ?.requestId
            ) !==
            normalizedRequestId
          ) {
            return;
          }

          const incomingMessage =
            event?.message &&
            typeof event.message ===
              "object"
              ? event.message
              : null;

          if (
            !incomingMessage
          ) {
            void loadMessages(
              true
            );

            return;
          }

          const incomingConversationId =
            String(
              event
                ?.conversationId ||
                incomingMessage
                  ?.requestConversationId ||
                ""
            );

          const currentConversationId =
            String(
              conversationIdRef.current ||
                ""
            );

          if (
            incomingConversationId &&
            currentConversationId &&
            incomingConversationId !==
              currentConversationId
          ) {
            return;
          }

          setMessageError(
            ""
          );

          setIsLoadingMessages(
            false
          );

          setChatMessages(
            (
              currentMessages
            ) =>
              mergeChatMessages(
                currentMessages,
                [
                  incomingMessage,
                ]
              )
          );
        };

      const handleConnect =
        () => {
          subscribe();

          if (
            realtimeWasDisconnectedRef.current
          ) {
            realtimeWasDisconnectedRef.current =
              false;

            void loadMessages(
              true
            );
          }
        };

      const handleDisconnect =
        () => {
          realtimeWasDisconnectedRef.current =
            true;
        };

      const handleConnectionError =
        (
          error
        ) => {
          console.warn(
            "[PendentLoadAuditPanel] Realtime connection error:",
            error?.message ||
              error
          );
        };

      socket.on(
        "carrier:request:subscribed",
        handleSubscribed
      );

      socket.on(
        "shipper:request:message-created",
        handleMessageCreated
      );

      socket.on(
        "connect",
        handleConnect
      );

      socket.on(
        "disconnect",
        handleDisconnect
      );

      socket.on(
        "connect_error",
        handleConnectionError
      );

      if (
        socket.connected
      ) {
        subscribe();
      } else {
        socket.connect();
      }

      return () => {
        disposed =
          true;

        controller.abort();

        unsubscribe();

        socket.off(
          "carrier:request:subscribed",
          handleSubscribed
        );

        socket.off(
          "shipper:request:message-created",
          handleMessageCreated
        );

        socket.off(
          "connect",
          handleConnect
        );

        socket.off(
          "disconnect",
          handleDisconnect
        );

        socket.off(
          "connect_error",
          handleConnectionError
        );
      };
    },
    [
      normalizedRequestId,
      t,
    ]
  );

  useEffect(
    () => {
      if (
        !isEmojiPickerOpen
      ) {
        return undefined;
      }

      const updateEmojiPickerPosition =
        () => {
          const triggerElement =
            emojiPickerRef.current;

          if (
            !triggerElement
          ) {
            return;
          }

          const rectangle =
            triggerElement.getBoundingClientRect();

          const pickerWidth =
            Math.min(
              EMOJI_PICKER_WIDTH,
              window.innerWidth -
                EMOJI_PICKER_VIEWPORT_PADDING *
                  2
            );

          const pickerHeight =
            Math.min(
              EMOJI_PICKER_HEIGHT,
              window.innerHeight -
                EMOJI_PICKER_VIEWPORT_PADDING *
                  2
            );

          const availableAbove =
            rectangle.top -
            EMOJI_PICKER_VIEWPORT_PADDING;

          const availableBelow =
            window.innerHeight -
            rectangle.bottom -
            EMOJI_PICKER_VIEWPORT_PADDING;

          const shouldOpenAbove =
            availableAbove >=
              pickerHeight +
                EMOJI_PICKER_GAP ||
            availableAbove >
              availableBelow;

          const requestedTop =
            shouldOpenAbove
              ? rectangle.top -
                pickerHeight -
                EMOJI_PICKER_GAP
              : rectangle.bottom +
                EMOJI_PICKER_GAP;

          const maximumTop =
            Math.max(
              EMOJI_PICKER_VIEWPORT_PADDING,
              window.innerHeight -
                pickerHeight -
                EMOJI_PICKER_VIEWPORT_PADDING
            );

          const nextTop =
            Math.min(
              Math.max(
                requestedTop,
                EMOJI_PICKER_VIEWPORT_PADDING
              ),
              maximumTop
            );

          const requestedLeft =
            rectangle.right -
            pickerWidth;

          const maximumLeft =
            Math.max(
              EMOJI_PICKER_VIEWPORT_PADDING,
              window.innerWidth -
                pickerWidth -
                EMOJI_PICKER_VIEWPORT_PADDING
            );

          const nextLeft =
            Math.min(
              Math.max(
                requestedLeft,
                EMOJI_PICKER_VIEWPORT_PADDING
              ),
              maximumLeft
            );

          setEmojiPickerPosition({
            top:
              nextTop,

            left:
              nextLeft,
          });
        };

      const handleOutsideClick =
        (
          event
        ) => {
          const clickedElement =
            event.target;

          const clickedTrigger =
            emojiPickerRef.current
              ?.contains(
                clickedElement
              );

          const clickedPopover =
            emojiPickerPopoverRef.current
              ?.contains(
                clickedElement
              );

          if (
            clickedTrigger ||
            clickedPopover
          ) {
            return;
          }

          setIsEmojiPickerOpen(
            false
          );
        };

      const handleKeyDown =
        (
          event
        ) => {
          if (
            event.key ===
            "Escape"
          ) {
            setIsEmojiPickerOpen(
              false
            );
          }
        };

      updateEmojiPickerPosition();

      window.addEventListener(
        "resize",
        updateEmojiPickerPosition
      );

      window.addEventListener(
        "scroll",
        updateEmojiPickerPosition,
        true
      );

      document.addEventListener(
        "mousedown",
        handleOutsideClick
      );

      document.addEventListener(
        "keydown",
        handleKeyDown
      );

      return () => {
        window.removeEventListener(
          "resize",
          updateEmojiPickerPosition
        );

        window.removeEventListener(
          "scroll",
          updateEmojiPickerPosition,
          true
        );

        document.removeEventListener(
          "mousedown",
          handleOutsideClick
        );

        document.removeEventListener(
          "keydown",
          handleKeyDown
        );
      };
    },
    [
      isEmojiPickerOpen,
    ]
  );

  const handleEmojiClick =
    (
      emojiData
    ) => {
      const emoji =
        String(
          emojiData?.emoji ||
            ""
        );

      if (
        !emoji
      ) {
        return;
      }

      const inputElement =
        messageInputRef.current;

      const selectionStart =
        Number.isInteger(
          inputElement
            ?.selectionStart
        )
          ? inputElement.selectionStart
          : messageText.length;

      const selectionEnd =
        Number.isInteger(
          inputElement
            ?.selectionEnd
        )
          ? inputElement.selectionEnd
          : selectionStart;

      const nextMessage =
        `${messageText.slice(
          0,
          selectionStart
        )}${emoji}${messageText.slice(
          selectionEnd
        )}`;

      const nextCursorPosition =
        selectionStart +
        emoji.length;

      setMessageText(
        nextMessage
      );

      window.requestAnimationFrame(
        () => {
          const currentInput =
            messageInputRef.current;

          if (
            !currentInput
          ) {
            return;
          }

          currentInput.focus();

          currentInput.setSelectionRange(
            nextCursorPosition,
            nextCursorPosition
          );
        }
      );
    };

  const handleSend =
    async () => {
      const nextMessage =
        String(
          messageText ||
            ""
        ).trim();

      if (
        !normalizedRequestId ||
        !nextMessage ||
        isSendingMessage
      ) {
        return;
      }

      setIsSendingMessage(
        true
      );

      setMessageError(
        ""
      );

      try {
        const response =
          await axios.post(
            `/carrier-request-conversations/${normalizedRequestId}/messages`,
            {
              message:
                nextMessage,
            }
          );

        const createdMessage =
          response.data
            ?.message &&
          typeof response.data
            .message ===
            "object"
            ? response.data
                .message
            : null;

        if (
          response.data
            ?.conversation
        ) {
          setConversation(
            response.data
              .conversation
          );
        }

        if (
          createdMessage
        ) {
          setChatMessages(
            (
              currentMessages
            ) =>
              mergeChatMessages(
                currentMessages,
                [
                  createdMessage,
                ]
              )
          );
        }

        setMessageText(
          ""
        );

        setIsEmojiPickerOpen(
          false
        );
      } catch (
        error
      ) {
        console.error(
          "[PendentLoadAuditPanel] Could not send chat message:",
          error
        );

        setMessageError(
          t(
            "auditPanel.errors.sendMessageFailed"
          )
        );
      } finally {
        setIsSendingMessage(
          false
        );
      }
    };

  return (
    <section className="shipment-v2-panel shipment-v2-audit-panel">
      <div className="shipment-v2-panel-header">
        <h2>
          {t(
            "auditPanel.title"
          )}
        </h2>

        <button
          className="shipment-v2-filter-btn"
          type="button"
          disabled
          title={
            t(
              "auditPanel.filterUnavailable"
            )
          }
        >
          <Filter
            size={
              15
            }
          />

          {t(
            "auditPanel.filter"
          )}
        </button>
      </div>

      <div className="edit-request-conversation-picker">
        <div className="edit-request-conversation-picker__header">
          <div>
            <strong>
              {t(
                "auditPanel.privateConversation"
              )}
            </strong>

            <span>
              {t(
                "auditPanel.privateConversationHelper"
              )}
            </span>
          </div>
        </div>

        {normalizedRequestId ? (
          <div className="edit-request-conversation-picker__selected">
            <span>
              {shipperInitials}
            </span>

            <strong>
              {shipperName}
            </strong>
          </div>
        ) : (
          <div className="edit-request-conversation-picker__empty">
            {t(
              "auditPanel.noShipperRequest"
            )}
          </div>
        )}
      </div>

      {showAudit && (
        <div className="shipment-v2-audit-list">
          {auditItems.map(
            (
              item
            ) => {
              const Icon =
                item.icon;

              return (
                <div
                  key={
                    item.title
                  }
                  className={`shipment-v2-audit-item ${item.type}`}
                >
                  <div className="shipment-v2-audit-icon">
                    <Icon
                      size={
                        16
                      }
                    />
                  </div>

                  <div>
                    <strong>
                      {item.title}
                    </strong>

                    <span>
                      {item.meta}
                    </span>
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}

      <div
        ref={
          chatScrollRef
        }
        className="shipment-v2-chat-list"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
      >
        {isLoadingMessages && (
          <div className="shipment-v2-chat-note">
            <Clock3
              size={
                13
              }
            />

            {t(
              "auditPanel.loadingConversation"
            )}
          </div>
        )}

        {!isLoadingMessages &&
          chatMessages.map(
            (
              message
            ) => {
              const senderType =
                String(
                  message
                    ?.senderType ||
                    ""
                ).toUpperCase();

              const messageType =
                String(
                  message
                    ?.messageType ||
                    ""
                ).toUpperCase();

              const isSystemMessage =
                senderType ===
                  "SYSTEM" ||
                messageType ===
                  "SYSTEM";

              const isOurMessage =
                [
                  "CARRIER",
                  "DISPATCHER",
                ].includes(
                  senderType
                );

              const messageClass =
                isSystemMessage
                  ? "system"
                  : isOurMessage
                    ? "user"
                    : "shipper";

              const senderName =
                String(
                  message
                    ?.senderName ||
                  message
                    ?.senderUserName ||
                  message
                    ?.senderUser
                    ?.name ||
                  message
                    ?.senderUser
                    ?.fullname ||
                  ""
                ).trim();

              const senderCompanyName =
                String(
                  message
                    ?.senderCompanyName ||
                  ""
                ).trim();

              const senderLabel =
                isSystemMessage
                  ? t(
                      "auditPanel.sender.system"
                    )
                  : isOurMessage
                    ? [
                        senderName,
                        senderCompanyName,
                      ]
                        .filter(
                          Boolean
                        )
                        .join(
                          " · "
                        ) ||
                      t(
                        "auditPanel.sender.yourTeam"
                      )
                    : senderName
                      ? [
                          senderName,
                          shipperName,
                        ]
                          .filter(
                            Boolean
                          )
                          .join(
                            " · "
                          )
                      : shipperName;

              const avatarLabel =
                isSystemMessage
                  ? "SYS"
                  : getInitials(
                      senderName ||
                      senderLabel
                    );

              return (
                <div
                  key={
                    message.id ||
                    `${message.createdAt}-${message.message}`
                  }
                  className={`shipment-v2-chat-message ${messageClass}`}
                >
                  <div className="shipment-v2-avatar">
                    {avatarLabel}
                  </div>

                  <div>
                    <div className="shipment-v2-chat-meta">
                      <strong>
                        {senderLabel}
                      </strong>

                      <span>
                        {formatDateTime(
                          message
                            .createdAt,
                          locale,
                          t(
                            "common.now"
                          )
                        )}
                      </span>
                    </div>

                    <p>
                      {message.message}
                    </p>

                    {isOurMessage &&
                      !isSystemMessage && (
                      <span className="shipment-v2-read-check">
                        ✓
                      </span>
                    )}
                  </div>
                </div>
              );
            }
          )}

        {!isLoadingMessages &&
          normalizedRequestId &&
          chatMessages.length ===
            0 &&
          !messageError && (
            <div className="shipment-v2-chat-note">
              <Info
                size={
                  13
                }
              />

              {t(
                "auditPanel.noMessages"
              )}
            </div>
          )}
      </div>

      {messageError && (
        <div className="shipment-v2-route-note">
          <Info
            size={
              15
            }
          />

          {messageError}
        </div>
      )}

      <div className="shipment-v2-message-box">
        <input
          ref={
            messageInputRef
          }
          type="text"
          value={
            messageText
          }
          placeholder={
            normalizedRequestId
              ? t(
                  "auditPanel.privateMessagePlaceholder",
                  {
                    shipperName,
                  }
                )
              : t(
                  "auditPanel.chatUnavailable"
                )
          }
          disabled={
            !normalizedRequestId ||
            isSendingMessage
          }
          maxLength={
            5000
          }
          onChange={(event) =>
            setMessageText(
              event.target.value
            )
          }
          onKeyDown={(event) => {
            if (
              event.key ===
                "Enter" &&
              !event.shiftKey
            ) {
              event.preventDefault();

              void handleSend();
            }
          }}
        />

        <button
          type="button"
          className="shipment-v2-icon-btn"
          aria-label={
            t(
              "auditPanel.attachFile"
            )
          }
          title={
            t(
              "auditPanel.attachmentsUnavailable"
            )
          }
          disabled
        >
          <Paperclip
            size={
              17
            }
          />
        </button>

        <div className="shipment-v2-emoji-picker">
          <button
            ref={
              emojiPickerRef
            }
            type="button"
            className={[
              "shipment-v2-icon-btn",

              isEmojiPickerOpen
                ? "is-active"
                : "",
            ]
              .filter(
                Boolean
              )
              .join(
                " "
              )}
            aria-label={
              t(
                "auditPanel.selectEmoji"
              )
            }
            aria-haspopup="dialog"
            aria-expanded={
              isEmojiPickerOpen
            }
            disabled={
              !normalizedRequestId ||
              isSendingMessage
            }
            onClick={() =>
              setIsEmojiPickerOpen(
                (
                  currentValue
                ) =>
                  !currentValue
              )
            }
          >
            <Smile
              size={
                17
              }
            />
          </button>

          {isEmojiPickerOpen &&
            typeof document !==
              "undefined" &&
            createPortal(
              <div
                ref={
                  emojiPickerPopoverRef
                }
                className="shipment-v2-emoji-picker__popover"
                role="dialog"
                aria-label={
                  t(
                    "auditPanel.selectEmoji"
                  )
                }
                style={{
                  top:
                    emojiPickerPosition.top,

                  left:
                    emojiPickerPosition.left,
                }}
              >
                <EmojiPicker
                  onEmojiClick={
                    handleEmojiClick
                  }
                  theme="light"
                  emojiStyle="native"
                  searchPlaceHolder="Pesquisar emoji..."
                  previewConfig={{
                    showPreview:
                      false,
                  }}
                  width={
                    EMOJI_PICKER_WIDTH
                  }
                  height={
                    EMOJI_PICKER_HEIGHT
                  }
                  lazyLoadEmojis={
                    true
                  }
                />
              </div>,
              document.body
            )}
        </div>

        <button
          type="button"
          className="shipment-v2-send-btn"
          aria-label={
            t(
              "auditPanel.sendMessage"
            )
          }
          disabled={
            !normalizedRequestId ||
            !messageText.trim() ||
            isSendingMessage
          }
          onClick={() =>
            void handleSend()
          }
        >
          <Send
            size={
              18
            }
          />
        </button>
      </div>

      <div className="shipment-v2-chat-note">
        <FileCheck2
          size={
            13
          }
        />

        {t(
          "auditPanel.realtimeSavedNote"
        )}
      </div>

      {!conversation &&
        normalizedRequestId &&
        !isLoadingMessages && (
          <div className="shipment-v2-chat-note">
            {t(
              "auditPanel.requestReference",
              {
                requestId:
                  normalizedRequestId,

                pendentLoadNumber:
                  String(
                    pendentLoadNumber
                  ).padStart(
                    5,
                    "0"
                  ),
              }
            )}
          </div>
        )}
    </section>
  );
}